/**
 * Pinia Store for Telegram Channels Management
 */
import { defineStore } from "pinia";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/firebase";
import type { GetAIModelsResponse } from "@shared/entities/AIModelsInterfaces";
import type {
  TelegramChannel,
  ListChannelsRequest,
  ListChannelsResponse,
  GetChannelRequest,
  GetChannelResponse,
  UpdateChannelRequest,
  UpdateChannelResponse,
  CreateChannelRequest,
  CreateChannelResponse,
  DeleteChannelRequest,
  DeleteChannelResponse,
  UpdateChannelPrioritiesRequest,
  ManageUserBanRequest,
  ManageUserBanResponse,
  AlertType,
  KickAfterUnit,
  MonitoringConfig,
  ChannelType,
  ListChannelUsersRequest,
  ListChannelUsersResponse,
} from "@shared/entities/Telegram/TelegramChannelInterfaces";
import { DEFAULT_MONITORING_CONFIG } from "@shared/entities/Telegram/TelegramChannelInterfaces";
import { images } from "@/mixins/images";

// Re-export types for use in components
export type {
  TelegramChannel,
  AlertType,
  KickAfterUnit,
  MonitoringConfig,
  ChannelType,
};
export { DEFAULT_MONITORING_CONFIG };

interface ChannelsState {
  channels: TelegramChannel[];
  loading: boolean;
  error: string | null;
  selectedChannel: TelegramChannel | null;
  availableAIModels: string[];
  aiModelsLastUpdated: Date | null;
  aiModelsIsStale: boolean;
  aiModelsIsHardcoded: boolean;
}

export const useChannelsStore = defineStore("channels", {
  state: (): ChannelsState => ({
    channels: [],
    loading: false,
    error: null,
    selectedChannel: null,
    availableAIModels: [],
    aiModelsLastUpdated: null as Date | null,
    aiModelsIsStale: false,
    aiModelsIsHardcoded: false,
  }),

  getters: {
    /**
     * Get active channels only
     */
    activeChannels: (state): TelegramChannel[] => {
      return state.channels.filter((channel) => channel.active);
    },

    /**
     * Get channels with monitoring enabled
     */
    monitoredChannels: (state): TelegramChannel[] => {
      return state.channels.filter((channel) => channel.monitoringEnabled);
    },

    /**
     * Get channel by ID
     */
    getChannelById: (state) => {
      return (id: string): TelegramChannel | undefined => {
        return state.channels.find((channel) => channel.id === id);
      };
    },
  },

  actions: {
    /**
     * Fetch all channels from Firebase
     */
    async fetchChannels(activeOnly = false): Promise<void> {
      this.loading = true;
      this.error = null;

      try {
        const listChannels = httpsCallable<
          ListChannelsRequest,
          ListChannelsResponse
        >(functions, "TelegramChannels-listTelegramChannels");

        const result = await listChannels({ activeOnly });
        this.channels = result.data.channels;
      } catch (error: any) {
        // Handle Firebase HttpsError specifically
        if (error.code) {
          this.error = `Error (${error.code}): ${error.message}`;
        } else {
          this.error = error.message || "Failed to fetch channels";
        }
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch a single channel by ID
     */
    async fetchChannel(channelId: string): Promise<TelegramChannel> {
      this.loading = true;
      this.error = null;

      try {
        const getChannel = httpsCallable<GetChannelRequest, GetChannelResponse>(
          functions,
          "TelegramChannels-getTelegramChannel"
        );

        const result = await getChannel({ channelId });
        this.selectedChannel = result.data.channel;

        // Update in the list if exists
        const index = this.channels.findIndex((c) => c.id === channelId);
        if (index !== -1) {
          this.channels[index] = result.data.channel;
        }

        return result.data.channel;
      } catch (error: any) {
        this.error = error.message || "Failed to fetch channel";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Create a new channel
     */
    async createChannel(
      channel: Omit<TelegramChannel, "lastFullCleanup">
    ): Promise<TelegramChannel> {
      this.loading = true;
      this.error = null;

      try {
        const createChannel = httpsCallable<
          CreateChannelRequest,
          CreateChannelResponse
        >(functions, "TelegramChannels-createTelegramChannel");

        const result = await createChannel({ channel });

        if (result.data.success) {
          this.channels.push(result.data.channel);
          return result.data.channel;
        } else {
          throw new Error("Failed to create channel");
        }
      } catch (error: any) {
        this.error = error.message || "Failed to create channel";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Update an existing channel
     */
    async updateChannel(
      channelId: string,
      updates: Partial<TelegramChannel>
    ): Promise<TelegramChannel> {
      this.loading = true;
      this.error = null;

      try {
        const updateChannel = httpsCallable<
          UpdateChannelRequest,
          UpdateChannelResponse
        >(functions, "TelegramChannels-updateTelegramChannel");

        const result = await updateChannel({ channelId, updates });

        if (result.data.success) {
          // Update in the list
          const index = this.channels.findIndex((c) => c.id === channelId);
          if (index !== -1) {
            this.channels[index] = result.data.channel;
          }

          // Update selected channel if it's the one being edited
          if (this.selectedChannel?.id === channelId) {
            this.selectedChannel = result.data.channel;
          }

          return result.data.channel;
        } else {
          throw new Error("Failed to update channel");
        }
      } catch (error: any) {
        this.error = error.message || "Failed to update channel";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Delete a channel
     */
    async deleteChannel(channelId: string): Promise<void> {
      this.loading = true;
      this.error = null;

      try {
        const deleteChannel = httpsCallable<
          DeleteChannelRequest,
          DeleteChannelResponse
        >(functions, "TelegramChannels-deleteTelegramChannel");

        const result = await deleteChannel({ channelId });

        if (result.data.success) {
          // Remove from the list
          const index = this.channels.findIndex((c) => c.id === channelId);
          if (index !== -1) {
            this.channels.splice(index, 1);
          }

          // Clear selected channel if it's the one being deleted
          if (this.selectedChannel?.id === channelId) {
            this.selectedChannel = null;
          }
        } else {
          throw new Error("Failed to delete channel");
        }
      } catch (error: any) {
        this.error = error.message || "Failed to delete channel";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Update multiple channel priorities in batch
     */
    async updatePriorities(
      updates: { channelId: string; priority: number }[]
    ): Promise<void> {
      this.loading = true;
      this.error = null;

      try {
        const updateTelegramChannelsOrder = httpsCallable<
          UpdateChannelPrioritiesRequest,
          { success: boolean }
        >(functions, "TelegramChannels-updateTelegramChannelPriorities");

        const result = await updateTelegramChannelsOrder({ updates });

        if (result.data.success) {
          // Update local state for each updated channel
          updates.forEach((update) => {
            const index = this.channels.findIndex(
              (c) => c.id === update.channelId
            );
            if (index !== -1) {
              this.channels[index].priority = update.priority;
            }
          });

          // Sort channels locally as well (descending priority)
          this.channels.sort((a, b) => {
            const pA = a.priority || 0;
            const pB = b.priority || 0;
            if (pB !== pA) return pB - pA;
            return a.name.localeCompare(b.name);
          });
        } else {
          throw new Error("Failed to update channel priorities");
        }
      } catch (error: any) {
        this.error = error.message || "Failed to update channel priorities";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Clear error
     */
    clearError(): void {
      this.error = null;
    },

    /**
     * Select a channel
     */
    selectChannel(channel: TelegramChannel | null): void {
      this.selectedChannel = channel;
    },

    /**
     * Fetch available AI models
     */
    async fetchAIModels(): Promise<string[]> {
      // If we already have models, return them to avoid unnecessary calls
      if (this.availableAIModels.length > 0) {
        return this.availableAIModels;
      }

      try {
        const getModels = httpsCallable<void, GetAIModelsResponse>(
          functions,
          "TelegramMonitoring-getAvailableAIModels"
        );
        const result = await getModels();
        if (result.data?.data) {
          const responseData = result.data.data;
          this.availableAIModels = responseData.models || [];
          this.aiModelsLastUpdated = responseData.lastUpdated
            ? new Date(responseData.lastUpdated)
            : null;
          this.aiModelsIsStale = responseData.isStale || false;
          this.aiModelsIsHardcoded = responseData.isHardcoded || false;
        }
        return this.availableAIModels;
      } catch (error) {
        return [];
      }
    },
    /**
     * Manage user ban in a channel (ban or unban)
     */
    async manageUserBan(
      channelId: string,
      username: string,
      action: "ban" | "unban",
      reason?: string
    ): Promise<void> {
      const manageUserBanFn = httpsCallable<
        ManageUserBanRequest,
        ManageUserBanResponse
      >(functions, "TelegramChannels-manageUserBan");

      const result = await manageUserBanFn({
        channelId,
        username,
        action,
        reason,
      });

      if (!result.data.success) {
        throw new Error(`Failed to ${action} user`);
      }
    },

    /**
     * Fetch users for a channel with pagination and filtering
     */
    async fetchChannelUsers(
      params: ListChannelUsersRequest
    ): Promise<ListChannelUsersResponse> {
      try {
        const listChannelUsersFn = httpsCallable<
          ListChannelUsersRequest,
          ListChannelUsersResponse
        >(functions, "TelegramChannels-listChannelUsers");

        const result = await listChannelUsersFn(params);

        // Resolve profile pictures using the images mixin (same as Leaderboard)
        const users = result.data.users;
        await Promise.all(
          users.map(async (user) => {
            if (!user.profile) {
              user.profile = { nickname: user.usernamePK };
            }

            if (user.profile.photo && !user.profile.photo.startsWith("http")) {
              user.profile.photo = await images.methods.getProfilePicture(
                user.profile.photo
              );
            } else if (!user.profile.photo) {
              user.profile.photo = await images.methods.getProfilePicture(
                "non-existent-path"
              );
            }
          })
        );

        return result.data;
      } catch (error: any) {
        console.error("Error fetching channel users:", error);
        throw error;
      }
    },
  },
});
